import * as React from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { CrossBorderBreakdown } from "@bcc/components/products/cross-border/price-breakdown";
import { CrossBorderNote } from "@bcc/components/products/cross-border/cross-border-note";
import { CountryFlag } from "@bcc/components/products/products-table/cells/CountryFlag";
import { Badge } from "@bcc/components/ui/badge";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@bcc/components/ui/form";
import { Input } from "@bcc/components/ui/input";
import {
  breakdownsToSkuPrices,
  computeRegionPriceBreakdowns,
  DEFAULT_TAX_RATES_BY_REGION,
  parseBagSizeToKg,
  vatRateToPercent,
  type RegionPriceBreakdowns,
  type TaxRatesByRegion,
} from "@bcc/lib/products/cross-border-tax";
import type { CrossBorderCountries } from "@shared/schemas/enums/crossBorder.enum";
import type { FormProduct } from "../types";
import { handleNumericFieldChange, setSingleVariantFormTestId } from "./single-variant-form.utils";

interface CrossBorderVariantPricingProps {
  index: number;
  shipTo: CrossBorderCountries[];
  homeRegion: CrossBorderCountries;
  ratesByRegion?: TaxRatesByRegion;
  isBagSizeVariantImported?: boolean;
  marginFloorError?: string | null;
}

const toNumber = (value: unknown): number => {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (typeof value === "string") {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const formatMoney = (value: number): string => value.toFixed(2);

// Cross-border destinations (DE/NL) are EUR-only, so the KS rate badge renders
// the amount with a euro prefix. Input fields keep the bare numeric value.
const formatEuro = (value: number): string => `€${formatMoney(value)}`;

// Imported cross-border prices are always treated as gross (VAT + KS inclusive):
// the roaster types the final bruto and the net/KS/VAT split is shown read-only.
const GROSS_FLAGS = { vatIncluded: true, ksIncluded: true } as const;

const countryName = (t: ReturnType<typeof useTranslation>["t"], region: CrossBorderCountries): string =>
  t(`views.productsTable.shipTo.countries.${region}`, { defaultValue: region });

/**
 * Auto-calculated, read-only price block for a single ship-to region other than
 * the roaster's home. The RRP leg takes the destination VAT + destination KS
 * (the end customer pays the coffee tax). The wholesale leg takes the origin
 * (home) VAT with NO destination KS — BRG lodges the Kaffeesteuer on the
 * roaster's behalf, so the wholesale invoice is identical across destinations.
 */
const DerivedRegionSection = ({
  breakdowns,
  index,
  msrpLabel,
  wholesaleLabel,
  name,
  homeName,
  t,
}: {
  breakdowns: RegionPriceBreakdowns;
  index: number;
  msrpLabel: string;
  wholesaleLabel: string;
  name: string;
  homeName: string;
  t: ReturnType<typeof useTranslation>["t"];
}) => {
  // The RRP leg carries the destination's KS, so a non-zero KS there means this
  // destination levies coffee tax — which BRG lodges on the wholesale side.
  const destinationLeviesKs = breakdowns.msrp.ks > 0;
  return (
    <div className="rounded-md border p-3 space-y-3" data-testid={setSingleVariantFormTestId(`cb-derived-${breakdowns.region}`, index)}>
      <div className="flex flex-wrap items-center gap-2">
        <CountryFlag countryCode={breakdowns.region} />
        <h5 className="text-sm font-medium">{name}</h5>
        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          <Badge variant="secondary" className="text-[10px] font-normal">
            {t("products.priceUpdate.crossBorder.autoCalculated")}
          </Badge>
          <Badge variant="secondary" className="text-[10px] font-normal">
            {t("products.priceUpdate.crossBorder.derivedRrpVat", { vat: vatRateToPercent(breakdowns.msrpVatRate) })}
          </Badge>
          <Badge variant="secondary" className="text-[10px] font-normal">
            {destinationLeviesKs
              ? t("products.priceUpdate.crossBorder.derivedWholesaleBrg")
              : t("products.priceUpdate.crossBorder.derivedWholesaleVat", { vat: vatRateToPercent(breakdowns.wholesaleVatRate) })}
          </Badge>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <p className="text-sm font-medium">{msrpLabel}</p>
          <Input
            type="number"
            value={formatMoney(breakdowns.msrp.total)}
            readOnly
            disabled
            testId={setSingleVariantFormTestId(`cb-derived-msrp-${breakdowns.region}`, index)}
          />
          <CrossBorderBreakdown
            breakdown={breakdowns.msrp}
            vatRate={breakdowns.msrpVatRate}
            field="msrp"
            getTestId={(part) => setSingleVariantFormTestId(`cb-derived-msrp-${breakdowns.region}-${part}`, index)}
          />
        </div>
        <div className="space-y-1">
          <p className="text-sm font-medium">{wholesaleLabel}</p>
          <Input
            type="number"
            value={formatMoney(breakdowns.wholesale.total)}
            readOnly
            disabled
            testId={setSingleVariantFormTestId(`cb-derived-wholesale-${breakdowns.region}`, index)}
          />
          <CrossBorderBreakdown
            breakdown={breakdowns.wholesale}
            vatRate={breakdowns.wholesaleVatRate}
            field="wholesalePrice"
            getTestId={(part) => setSingleVariantFormTestId(`cb-derived-wholesale-${breakdowns.region}-${part}`, index)}
            note={destinationLeviesKs ? t("products.priceUpdate.crossBorder.brgLodgesNote", { home: homeName }) : undefined}
          />
        </div>
      </div>
    </div>
  );
};

/**
 * Cross-border price section for a single imported variant. The roaster's home
 * region is editable (gross MSRP/wholesale with a live Netto/KS/VAT breakdown);
 * every other ship-to region is auto-derived and read-only. The derived
 * per-region prices are written to the variant's `skuPrices` so they persist
 * into the product target metadata.
 */
export const CrossBorderVariantPricing = ({
  index,
  shipTo,
  homeRegion,
  ratesByRegion = DEFAULT_TAX_RATES_BY_REGION,
  isBagSizeVariantImported = false,
  marginFloorError,
}: CrossBorderVariantPricingProps) => {
  const { t } = useTranslation();
  const form = useFormContext<FormProduct>();

  const enteredMsrp = form.watch(`variants.${index}.price`);
  const enteredWholesale = form.watch(`variants.${index}.wholesalePrice`);
  const bagSize = form.watch(`variants.${index}.bagSize`);

  const weightKg = React.useMemo(() => parseBagSizeToKg(bagSize), [bagSize]);

  const breakdowns = React.useMemo(
    () =>
      computeRegionPriceBreakdowns({
        msrp: toNumber(enteredMsrp),
        wholesalePrice: toNumber(enteredWholesale),
        flags: GROSS_FLAGS,
        homeRegion,
        shipTo,
        weightKg,
        ratesByRegion,
      }),
    [enteredMsrp, enteredWholesale, weightKg, homeRegion, shipTo, ratesByRegion]
  );

  const homeBreakdowns = breakdowns.find((entry) => entry.region === homeRegion);
  const derivedBreakdowns = breakdowns.filter((entry) => entry.region !== homeRegion);

  // Persisted per-region prices: RRP keeps its KS-inclusive gross; wholesale is
  // sent KS-excluded (netto + VAT). BRG lodges the coffee tax for every
  // destination, including the home region — see breakdownsToSkuPrices.
  const skuPrices = React.useMemo(
    () => breakdownsToSkuPrices(breakdowns, toNumber(enteredWholesale), homeRegion),
    [breakdowns, enteredWholesale, homeRegion]
  );

  // Persist the derived per-region prices onto the variant so setTargetMetadata
  // picks them up. Guarded by a value comparison to avoid a render loop.
  React.useEffect(() => {
    const current = form.getValues(`variants.${index}.skuPrices`);
    if (JSON.stringify(current) !== JSON.stringify(skuPrices)) {
      form.setValue(`variants.${index}.skuPrices`, skuPrices, { shouldDirty: false, shouldValidate: false });
    }
  }, [skuPrices, form, index]);

  const msrpLabel = t("products.priceUpdate.crossBorder.rrp");
  const wholesaleLabel = t("products.priceUpdate.crossBorder.wholesaleExclTax");
  const homeRates = ratesByRegion[homeRegion];

  return (
    <div className="space-y-4" data-testid={setSingleVariantFormTestId(`cb-home-${homeRegion}`, index)}>
      <div className="flex flex-wrap items-center gap-2 border-b pb-2">
        <CountryFlag countryCode={homeRegion} />
        <h4 className="text-sm font-semibold">{t("products.priceUpdate.shippingToHeading", { country: countryName(t, homeRegion) })}</h4>
        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          <Badge variant="secondary" className="text-[10px] font-normal" testId={setSingleVariantFormTestId("cb-home-ks-badge", index)}>
            {homeRates.ksPerKg > 0
              ? t("products.priceUpdate.crossBorder.ksBadge", { ks: formatEuro(homeRates.ksPerKg) })
              : t("products.priceUpdate.crossBorder.noKsBadge")}
          </Badge>
          <Badge variant="secondary" className="text-[10px] font-normal">
            {t("products.priceUpdate.crossBorder.vatBadge", { vat: vatRateToPercent(homeRates.vatRate) })}
          </Badge>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name={`variants.${index}.price`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {msrpLabel}{" "}
                <span className="text-destructive" aria-hidden="true">
                  *
                </span>
              </FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="0.01"
                  {...field}
                  value={field.value ?? ""}
                  onChange={handleNumericFieldChange(field.onChange)}
                  testId={setSingleVariantFormTestId("cb-msrp", index)}
                  disabled={isBagSizeVariantImported}
                  aria-invalid={!!marginFloorError}
                />
              </FormControl>
              {homeBreakdowns && (
                <CrossBorderBreakdown
                  breakdown={homeBreakdowns.msrp}
                  vatRate={homeBreakdowns.msrpVatRate}
                  field="msrp"
                  getTestId={(part) => setSingleVariantFormTestId(`cb-msrp-${part}`, index)}
                />
              )}
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={`variants.${index}.wholesalePrice`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {wholesaleLabel}{" "}
                <span className="text-destructive" aria-hidden="true">
                  *
                </span>
              </FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="0.01"
                  {...field}
                  value={field.value ?? ""}
                  onChange={handleNumericFieldChange(field.onChange)}
                  testId={setSingleVariantFormTestId("cb-wholesale", index)}
                  disabled={isBagSizeVariantImported}
                  aria-invalid={!!marginFloorError}
                />
              </FormControl>
              {homeBreakdowns && (
                <CrossBorderBreakdown
                  breakdown={homeBreakdowns.wholesale}
                  vatRate={homeBreakdowns.wholesaleVatRate}
                  field="wholesalePrice"
                  getTestId={(part) => setSingleVariantFormTestId(`cb-wholesalePrice-${part}`, index)}
                />
              )}
              {!marginFloorError && <FormMessage />}
            </FormItem>
          )}
        />
        {marginFloorError && (
          <div className="flex items-center gap-1.5 col-span-2 w-full">
            <span className="flex items-center justify-center h-4 w-4 rounded-full bg-destructive text-white text-[10px] font-bold shrink-0">
              !
            </span>
            <span className="text-xs font-semibold text-destructive">{marginFloorError}</span>
          </div>
        )}
      </div>

      {derivedBreakdowns.map((entry) => (
        <DerivedRegionSection
          key={entry.region}
          breakdowns={entry}
          index={index}
          msrpLabel={msrpLabel}
          wholesaleLabel={wholesaleLabel}
          name={t("products.priceUpdate.shippingToHeading", { country: countryName(t, entry.region) })}
          homeName={countryName(t, homeRegion)}
          t={t}
        />
      ))}

      <CrossBorderNote homeLeviesKs={homeRates.ksPerKg > 0} testId={setSingleVariantFormTestId("cb-note", index)} />
    </div>
  );
};
